import store from '../Redux/store';
import {showSnackbar} from "../Redux/actions/snackbar";
import { hideLoadingBar, showLoadingBar } from "../Redux/actions/loading";
import {instanceConfigured, instanceError} from "../Redux/actions/instsance";
import axios from "axios/index";
import * as Sentry from '@sentry/browser';
const packageJson = require('../../package.json');

class FatalError extends Error {}
class SnackError extends Error {}

/**
 * Host based on subdomains
 */
const getHost = () => {
    if (localStorage.getItem('host')) {
        return localStorage.getItem('host');
    }

    let hostname = window.location.hostname.toLowerCase();
    const hostnameSections = hostname.split(".");
    const instanceName = hostnameSections[0];
    
    Sentry.setTag('instanceName', instanceName);
    Sentry.setTag('versionNumber', packageJson.version);

    return `https://${instanceName}.sistemapowergym.com/api/`;
};

/**
 * Default paramters for axios
 * @type {AxiosInstance}
 */
const powergym = axios.create({
    baseURL: getHost(),
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

/**
 *
 * @param response
 * @returns {*}
 */
const handleAPIResponse = (response) => {

    store.dispatch(hideLoadingBar());

    if (!response) {
        throw new FatalError("Error de comunicación. Compruebe su conexión a Internet.");
    }
    
    if (typeof response.data.code === 'undefined') {
        throw new FatalError("Missing status field");
    }
    
    if (typeof response.data.success === 'undefined') {
        throw new FatalError("Missing success field");
    }

    if (response.data.status === 403) {
        logout();
        throw new SnackError("Por favor, inicie sesión...");
    }
    
    return response.data;
};

/**
 *
 * @param error
 * @returns {*}
 */
const handleAPIError = (error) => {
    
    store.dispatch(hideLoadingBar());

    if (! error.response) {
        return store.dispatch(instanceError({
            humanError: "No pudimos establecer conexión con el servidor por problemas en la red. Intentelo más tarde.",
            devError: error.toString()
        }));
    }

    if (error instanceof FatalError) {
        return store.dispatch(instanceError({
            humanError: "Hemos detectado un problema. Reintente más tarde.",
            devError: error.toString()
        }));
    }

    if (error instanceof SnackError) {
        return store.dispatch(showSnackbar(error.toString()));
    }

    if (error.response && error.response.data) {
        //We have a response
        return error.response.data;
    }
};

/**
 *
 * @returns {string}
 */
const getAuthStr = () => {
    let user = JSON.parse(localStorage.getItem('user'));
    return `?user_id=${user.id}&access_token=${user.access_token}`;
};


/**
 * Get instance data. First time hits on API endpoint and then store in storage
 * @param forceLoad
 * @returns {*}
 */
async function getInstance() {
    try {
        const res = await powergym.get("instance");

        const responseData = handleAPIResponse(res);

        if (typeof responseData.branch === 'undefined') {
            Sentry.captureMessage('Error on server branch');
            throw new FatalError('Error on server branch');
        }
        
        store.dispatch(instanceConfigured({
            ...responseData.branch,
            host: getHost()
        }));

        return responseData;

    } catch (error) {
        handleAPIError(new FatalError(error));
    }
}

/**
 * Login
 * @param dni
 * @param password
 * @returns {Promise<Response>}
 */
async function login(dni, password) {
    try {
        store.dispatch(showLoadingBar());

        const loginData = {
            dni,
            password
        };

        const res = await powergym.post(`auth`, loginData);
        const responseData = handleAPIResponse(res);

        if (responseData.success !== true || typeof responseData.data.user === 'undefined') {
            throw new SnackError(res.message || "Login error");
        }

        localStorage.setItem('user', JSON.stringify({
            ...responseData.data.user,
            dni: dni
        }));

        Sentry.setUser({ username: dni })

        return responseData;

    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 * Logout
 */
function logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('user');
}


/**
 *
 * @returns {Promise<Response>}
 */
async function profile() {
    try {
        const res = await powergym.get(`me${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}


/**
 *
 * @returns {Promise<Response>}
 */
async function myBookings() {
    try {
        const res = await powergym.get(`my-bookings${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 *
 * @returns {Promise<Response>}
 */
async function routine() {
    try {
        const res = await powergym.get(`routine${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 *
 * @returns {Promise<Response>}
 */
async function getSchedules() {
    try {
        const res = await powergym.get(`schedules${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 *
 * @returns {Promise<Response>}
 */
async function getOnlineBookingsPerSchedule() {
    try {
        const res = await powergym.get(`online-bookings-by-schedule${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}


/**
 *
 * @param schedule_id
 * @param date
 * @returns {Promise<Response>}
 */
async function getTimetables(schedule_id, date) {
    try {
        const res = await powergym.get(`timetables${getAuthStr()}&schedule_id=${schedule_id}&date=${date}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}


const book = async ({timetable_id, date} = {}) => {
    try {
        const res = await powergym.post(`book${getAuthStr()}&timetable_id=${timetable_id}&date=${date}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
};

/**
 *
 * @param id
 * @returns {Promise<AxiosResponse<any>>}
 */
const removeBooking = async (id) => {
    try {
        const res = await powergym.delete(`bookings/${id}${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
};


export const PGService = {
    login,
    logout,
    profile,
    myBookings,
    routine,
    getInstance,
    getSchedules,
    getOnlineBookingsPerSchedule,
    getTimetables,
    removeBooking,
    book,
};
